// Portfolio Administration v1.0 PR 4b §7.8 — modal that opens a new
// DefaultCase from the empty-state CTA. Captures the 7 documented
// trigger types + a justification. The backend (DefaultCaseService)
// also auto-opens cases from worker-detected events; this modal is the
// manual escape hatch for B3 / A3 / E1 / ADMIN.

import { useState } from "react";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useOpenDefaultCase } from "@/hooks/portfolioAdmin/usePr4b";
import type { DefaultCaseTrigger } from "@/api/portfolioAdmin/pr4bTypes";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface OpenDefaultCaseModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
  onOpened?: (caseId: string) => void;
}

const TRIGGERS: { value: DefaultCaseTrigger; label: string }[] = [
  { value: "payment-delinquency", label: "Payment delinquency" },
  { value: "covenant-failure", label: "Covenant failure" },
  { value: "insurance-lapse", label: "Insurance lapse" },
  { value: "UCC-lapse", label: "UCC lapse" },
  { value: "borrower-bankruptcy", label: "Borrower bankruptcy" },
  { value: "fraud", label: "Fraud" },
  { value: "voluntary", label: "Voluntary (borrower-initiated)" },
];

type Phase = "form" | "confirm";

export function OpenDefaultCaseModal({
  loanId,
  open,
  onClose,
  onOpened,
}: OpenDefaultCaseModalProps) {
  const tz = useUserTimeZone();
  const [phase, setPhase] = useState<Phase>("form");
  const [triggeredBy, setTriggeredBy] = useState<DefaultCaseTrigger>(
    "payment-delinquency",
  );
  const [triggerDate, setTriggerDate] = useState(() =>
    isoDateInTz(new Date(), tz),
  );
  const [description, setDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openMutation = useOpenDefaultCase(loanId);

  const reset = () => {
    setPhase("form");
    setTriggeredBy("payment-delinquency");
    setTriggerDate(isoDateInTz(new Date(), tz));
    setDescription("");
    setErrorMessage(null);
  };

  const handleClose = () => {
    if (openMutation.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    try {
      const result = await openMutation.mutateAsync({
        triggeredBy,
        triggerDate: new Date(triggerDate).toISOString(),
        description,
      });
      if (onOpened) onOpened(result.id);
      reset();
      onClose();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to open default case.",
      );
    }
  };

  const formInvalid = !triggeredBy || !triggerDate || description.trim().length < 10;

  const footer =
    phase === "form" ? (
      <>
        <Button variant="tertiary" size="sm" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setPhase("confirm")}
          disabled={formInvalid}
          data-testid="open-default-case-continue"
        >
          Continue
        </Button>
      </>
    ) : (
      <>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => setPhase("form")}
          disabled={openMutation.isPending}
        >
          Back
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleSubmit}
          disabled={openMutation.isPending}
          data-testid="open-default-case-confirm"
        >
          {openMutation.isPending ? "Opening…" : "Open default case"}
        </Button>
      </>
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      dismissOnBackdropClick={false}
      title={
        phase === "form" ? "Open default case" : "Confirm — irreversible action"
      }
      description={
        phase === "form"
          ? "This will set the loan account state to 'defaulted'."
          : undefined
      }
      footer={footer}
    >
      {phase === "form" && (
        <div className="flex flex-col gap-space-3" data-testid="open-default-case-form">
          <div>
            <Label htmlFor="default-trigger">Triggered by</Label>
            <select
              id="default-trigger"
              className="h-10 px-space-2 w-full border border-border-light rounded-md text-small bg-white-pure"
              value={triggeredBy}
              onChange={(e) =>
                setTriggeredBy(e.target.value as DefaultCaseTrigger)
              }
            >
              {TRIGGERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="default-trigger-date">Trigger date</Label>
            <Input
              id="default-trigger-date"
              type="date"
              value={triggerDate}
              onChange={(e) => setTriggerDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="default-description">
              Description (≥ 10 characters)
            </Label>
            <Textarea
              id="default-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., NSF letter received 2026-05-08; 28 days past due on May payment"
            />
          </div>
          {errorMessage && (
            <p className="text-small text-status-error m-0" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
      )}
      {phase === "confirm" && (
        <div className="flex flex-col gap-space-3">
          <p className="text-body m-0">
            Opening this case will set the loan account state to{" "}
            <strong>defaulted</strong>. The DefaultCase lifecycle state
            machine will be initiated, a WorkoutEvent log will start
            accumulating, and a CurePeriod may be auto-started depending
            on the trigger.
          </p>
          <dl className="border border-border-light rounded-md p-space-3 text-small">
            <div className="flex gap-space-2">
              <dt className="font-bold w-32">Trigger</dt>
              <dd className="m-0">
                {TRIGGERS.find((t) => t.value === triggeredBy)?.label}
              </dd>
            </div>
            <div className="flex gap-space-2 mt-space-1">
              <dt className="font-bold w-32">Trigger date</dt>
              <dd className="m-0">{triggerDate}</dd>
            </div>
            <div className="flex gap-space-2 mt-space-1">
              <dt className="font-bold w-32 shrink-0">Description</dt>
              <dd className="m-0 whitespace-pre-wrap">{description}</dd>
            </div>
          </dl>
          {errorMessage && (
            <p className="text-small text-status-error m-0" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
